import React, { useState } from 'react';
import { Select, FormControl, Box } from '@mui/material';
import { colors } from '../../../styles/fontStyles';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';

type DropdownSelectWrapperProps = {
  renderValueFn: (selected: string, open: boolean) => string;
  children: React.ReactNode;
  onChangeFn: (compound: string) => void;
  initialSelectedValue?: string;
};

const DropdownSelectWrapper: React.FC<DropdownSelectWrapperProps> = ({
  renderValueFn,
  onChangeFn,
  initialSelectedValue,
  children,
}) => {
  const [selected, setSelected] = useState<string>(initialSelectedValue || '');
  const [open, setOpen] = useState(false);
  const { theme } = useThemeContext();

  const onChangeSelect = (e: any) => {
    setSelected(e.target.value);
    onChangeFn(e.target.value);
  };

  return (
    <Box sx={{ minWidth: 120 }}>
      <FormControl
        sx={{
          '& .MuiInputBase-root': {
            height: '34px',
            width: '137px',
            fontSize: '0.9rem',
            color: theme.custom.report.timeline.dropdownWrapper.root.color,
          },
          '& .MuiInputBase-input': {
            paddingRight: '0px !important',
          },
          // Better to override styles
          // as the IconComponent prop click event was not working as the
          // original icon component is
          '& .MuiSelect-icon': {
            backgroundImage: `url('/icons/chevron_down_gray.svg')`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            width: '24px',
            height: '24px',
            left: '6px',
            // Hides the default SVG
            '& > *': {
              display: 'none',
            },
          },
          '& .MuiSelect-iconOpen': {
            transform: 'rotate(180deg)',
          },
        }}
      >
        <Select
          displayEmpty
          value={selected}
          onChange={onChangeSelect}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          renderValue={(selected) => renderValueFn(selected, open)}
          sx={{
            // display this color when a valud that is not compound is selected
            // bgcolor: colors.m3SysDarkPrimary,
            borderRadius: 2,
            // Menu Select not selected, nor active
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'solid 2px',
              borderColor: theme.custom.report.timeline.dropdownWrapper.select.notchedOutline.default.borderColor,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.custom.report.timeline.dropdownWrapper.select.notchedOutline.hover.borderColor,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor:
                theme.custom.report.timeline.dropdownWrapper.select.notchedOutline.focused.borderColor + ' !important',
            },
            '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
              borderColor:
                theme.custom.report.timeline.dropdownWrapper.select.notchedOutline.focused.hover.borderColor +
                ' !important',
            },
            '& .MuiSelect-select': {
              color: theme.custom.report.timeline.dropdownWrapper.select.paper.color,
              fontWeight: 600,
              padding: '8px 32px',
            },
            // Ensure menu stays anchored below
            '& .MuiMenu-list': {
              paddingTop: '20px',
              paddingBottom: 0,
            },
            // customization of the icon
            '& .MuiSelect-icon': {
              backgroundImage: `url(` + theme.custom.report.timeline.dropdownWrapper.select.iconImageSrc + `)`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              width: '24px',
              height: '24px',
              right: '10px',
            },
            '& .MuiSelect-iconOpen': {
              transform: 'rotate(180deg)',
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                '& .Mui-selected': {
                  backgroundColor:
                    theme.custom.report.timeline.dropdownWrapper.select.paper.selectedOption.backgroundColor +
                    ' !important',
                },
                maxHeight: 300,
                color: theme.custom.report.timeline.dropdownWrapper.select.paper.color,
                backgroundColor: theme.custom.report.timeline.dropdownWrapper.select.paper.backgroundColor,
                // by default it has a background image, and affects the bg color
                backgroundImage: 'none',
                border: 'solid 1px',
                borderColor: theme.custom.report.timeline.dropdownWrapper.select.notchedOutline.default.borderColor,
              },
            },
            // Anchor the menu to the select component
            // so it's always shown below the select
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
        >
          {children}
        </Select>
      </FormControl>
    </Box>
  );
};

export default DropdownSelectWrapper;
